import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/hooks/use-language";
import { Link } from "react-router-dom";
import { Cookie, Settings, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

const CONSENT_KEY = "cookie_consent";
const CONSENT_VERSION = 1;
// CNIL/EDPB guidance: refresh consent at most every 13 months.
const CONSENT_MAX_AGE_MS = 1000 * 60 * 60 * 24 * 365;
export const OPEN_COOKIE_SETTINGS_EVENT = "open-cookie-settings";

export interface CookieConsent {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
  version: number;
}

function getStoredConsent(): CookieConsent | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsent;
    if (parsed.version !== CONSENT_VERSION) return null;
    const age = Date.now() - new Date(parsed.timestamp).getTime();
    if (Number.isNaN(age) || age > CONSENT_MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function storeConsent(consent: CookieConsent) {
  localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
  window.dispatchEvent(new CustomEvent("cookie-consent-change", { detail: consent }));
}

export function CookieBanner() {
  const { t } = useTranslation();
  const { localizedPath } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const consent = getStoredConsent();
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    const open = () => {
      const existing = getStoredConsent();
      if (existing) {
        setAnalytics(existing.analytics);
        setMarketing(existing.marketing);
      }
      setShowDetails(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, open);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, open);
  }, []);

  const handleAcceptAll = useCallback(() => {
    storeConsent({
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION,
    });
    setVisible(false);
  }, []);

  const handleRejectAll = useCallback(() => {
    storeConsent({
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION,
    });
    setVisible(false);
  }, []);

  const handleSavePreferences = useCallback(() => {
    storeConsent({
      necessary: true,
      analytics,
      marketing,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION,
    });
    setVisible(false);
  }, [analytics, marketing]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[100] flex justify-center pointer-events-none"
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-banner-title"
    >

      {/* Banner */}
      <div
        className={cn(
          "relative pointer-events-auto w-full max-w-2xl mx-4 mb-4 rounded-2xl border border-border bg-card shadow-clinical-xl p-6 animate-fade-up"
        )}
      >
        {/* Header */}
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 rounded-xl bg-primary/10">
            <Cookie className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 id="cookie-banner-title" className="font-semibold text-foreground text-base">
              {t("cookieBanner.title")}
            </h3>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
              {t("cookieBanner.description")}{" "}
              <Link
                to={localizedPath("/privacy")}
                className="underline underline-offset-2 hover:text-foreground transition-colors"
              >
                {t("cookieBanner.privacyLink")}
              </Link>
            </p>
          </div>
        </div>

        {/* Expandable details */}
        {showDetails && (
          <div className="mb-4 space-y-3 border-t border-border pt-4">
            {/* Necessary */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">
                  {t("cookieBanner.necessary")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("cookieBanner.necessaryDesc")}
                </p>
              </div>
              <Switch checked disabled className="opacity-70" />
            </div>

            {/* Analytics */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">
                  {t("cookieBanner.analytics")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("cookieBanner.analyticsDesc")}
                </p>
              </div>
              <Switch checked={analytics} onCheckedChange={setAnalytics} />
            </div>

            {/* Marketing */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">
                  {t("cookieBanner.marketing")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("cookieBanner.marketingDesc")}
                </p>
              </div>
              <Switch checked={marketing} onCheckedChange={setMarketing} />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          {!showDetails ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRejectAll}
                className="gap-1.5"
              >
                <X className="h-3.5 w-3.5" />
                {t("cookieBanner.rejectAll")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDetails(true)}
                className="gap-1.5"
              >
                <Settings className="h-3.5 w-3.5" />
                {t("cookieBanner.customize")}
              </Button>
              <Button
                size="sm"
                onClick={handleAcceptAll}
                className="gap-1.5 sm:ml-auto"
              >
                <Check className="h-3.5 w-3.5" />
                {t("cookieBanner.acceptAll")}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDetails(false)}
              >
                {t("cookieBanner.back")}
              </Button>
              <Button
                size="sm"
                onClick={handleSavePreferences}
                className="gap-1.5 sm:ml-auto"
              >
                <Check className="h-3.5 w-3.5" />
                {t("cookieBanner.savePreferences")}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Utility: read consent from anywhere */
export function getCookieConsent(): CookieConsent | null {
  return getStoredConsent();
}
